import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../api/endpoints';
import { DashboardData } from '../../types';
import {
  FiShoppingCart,
  FiClock,
  FiTruck,
  FiFileText,
  FiBox,
  FiAlertTriangle,
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { SkeletonStats, SkeletonCard } from '../../components/common/Skeleton';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

const STATUS_COLORS: Record<string, string> = {
  Draft: '#94a3b8',
  Submitted: '#f59e0b',
  Accepted: '#10b981',
  Rejected: '#ef4444',
  Paid: '#6366f1',
  New: '#3b82f6',
  Acknowledged: '#06b6d4',
  Shipped: '#8b5cf6',
  Closed: '#64748b',
  Cancelled: '#dc2626',
};

const STATUS_LABELS: Record<string, string> = {
  Draft: 'Szkic',
  Submitted: 'Wysłane',
  Accepted: 'Zaakceptowane',
  Rejected: 'Odrzucone',
  Paid: 'Zapłacone',
  New: 'Nowe',
  Acknowledged: 'Potwierdzone',
  Shipped: 'Wysłane',
  Closed: 'Zamknięte',
  Cancelled: 'Anulowane',
  InTransit: 'W transporcie',
  Delivered: 'Dostarczone',
};

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await dashboardApi.get();
      setData(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd pobierania danych');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  const handleActivityClick = (type: string, entityId: string | null) => {
    if (!entityId) return;
    switch (type) {
      case 'PurchaseOrder': navigate(`/purchase-orders/${entityId}`); break;
      case 'Shipment': navigate(`/shipments/${entityId}`); break;
      case 'Invoice': navigate(`/invoices/${entityId}`); break;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Dashboard</h1></div>
        <SkeletonStats count={6} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', margin: '20px 0' }}>
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
          <SkeletonCard /><SkeletonCard />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">Brak danych dashboardu.</div>
      </div>
    );
  }

  const hasOverdue = data.overdueInvoiceCount > 0;
  const hasRejected = data.rejectedInvoiceCount > 0;

  const stats = [
    { label: 'Zamówienia', value: data.totalPurchaseOrders, icon: <FiShoppingCart />, color: 'blue', link: '/purchase-orders' },
    { label: 'Oczekujące PO', value: data.pendingOrders, icon: <FiClock />, color: 'orange', link: '/purchase-orders?status=pending' },
    { label: 'Aktywne wysyłki', value: data.activeShipments, icon: <FiTruck />, color: 'green', link: '/shipments' },
    { label: 'Oczekujące faktury', value: data.pendingInvoices, icon: <FiFileText />, color: 'orange', link: '/invoices' },
    { label: 'Produkty', value: data.totalProducts, icon: <FiBox />, color: 'blue', link: '/products' },
    { label: 'Niski stan', value: data.lowStockProducts, icon: <FiAlertTriangle />, color: data.lowStockProducts > 0 ? 'red' : 'green', link: '/products' },
  ];

  const invoicePieData = data.invoiceStatusBreakdown.map(s => ({
    name: STATUS_LABELS[s.status] || s.status,
    value: s.count,
    fill: STATUS_COLORS[s.status] || '#94a3b8',
  }));

  const orderPieData = data.orderStatusBreakdown.map(s => ({
    name: STATUS_LABELS[s.status] || s.status,
    value: s.count,
    fill: STATUS_COLORS[s.status] || '#94a3b8',
  }));

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Dashboard</h1>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => fetchData(true)}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? 'spin-animation' : ''} />
            {refreshing ? 'Odświeżam...' : 'Odśwież'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {(hasOverdue || hasRejected) && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {hasOverdue && (
            <div
              className="bulk-actions-bar"
              style={{
                background: '#fef2f2',
                borderColor: '#ef4444',
                cursor: 'pointer',
                flex: 1,
                minWidth: '280px',
              }}
              onClick={() => navigate('/invoices')}
            >
              <FiAlertTriangle style={{ color: '#ef4444', fontSize: '20px' }} />
              <div>
                <div style={{ fontWeight: 700, color: '#dc2626' }}>
                  {data.overdueInvoiceCount} {data.overdueInvoiceCount === 1 ? 'faktura przeterminowana' : 'faktur przeterminowanych'}
                </div>
                <div style={{ fontSize: '12px', color: '#b91c1c' }}>
                  Łączna wartość: {formatCurrency(data.overdueInvoiceValue)}
                </div>
              </div>
            </div>
          )}
          {hasRejected && (
            <div
              className="bulk-actions-bar"
              style={{
                background: '#fff7ed',
                borderColor: '#f97316',
                cursor: 'pointer',
                flex: 1,
                minWidth: '280px',
              }}
              onClick={() => navigate('/invoices')}
            >
              <FiXCircle style={{ color: '#f97316', fontSize: '20px' }} />
              <div>
                <div style={{ fontWeight: 700, color: '#ea580c' }}>
                  {data.rejectedInvoiceCount} {data.rejectedInvoiceCount === 1 ? 'faktura odrzucona' : 'faktur odrzuconych'}
                </div>
                <div style={{ fontSize: '12px', color: '#c2410c' }}>
                  Wymagają korekty i ponownego wysłania
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats grid */}
      <div className="stats-grid">
        {stats.map((stat, i) => (
          <div
            key={i}
            className="stat-card"
            onClick={() => navigate(stat.link)}
            style={{ cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
            }}
          >
            <div className={`stat-icon ${stat.color}`}>
              {stat.icon}
            </div>
            <div className="stat-info">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Financial Summary Bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', margin: '20px 0' }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <FiDollarSign style={{ fontSize: '24px', color: '#6366f1', marginBottom: '4px' }} />
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Wartość zamówień
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)' }}>
              {formatCurrency(data.totalOrderValue)}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <FiCheckCircle style={{ fontSize: '24px', color: '#10b981', marginBottom: '4px' }} />
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Zafakturowano
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text)' }}>
              {formatCurrency(data.totalInvoicedValue)}
            </div>
          </div>
        </div>
        <div className="card" style={hasOverdue ? { borderLeft: '3px solid #ef4444' } : {}}>
          <div className="card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <FiAlertTriangle style={{ fontSize: '24px', color: hasOverdue ? '#ef4444' : '#94a3b8', marginBottom: '4px' }} />
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Przeterminowane
            </div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: hasOverdue ? '#ef4444' : 'var(--text)' }}>
              {formatCurrency(data.overdueInvoiceValue)}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice funnel quick stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', margin: '0 0 20px' }}>
        {[
          { label: 'Szkice', count: data.draftInvoiceCount, color: '#94a3b8', bg: '#f8fafc' },
          { label: 'Wysłane', count: data.submittedInvoiceCount, color: '#f59e0b', bg: '#fffbeb' },
          { label: 'Zaakceptowane', count: data.acceptedInvoiceCount, color: '#10b981', bg: '#ecfdf5' },
          { label: 'Odrzucone', count: data.rejectedInvoiceCount, color: '#ef4444', bg: '#fef2f2' },
        ].map((s, i) => (
          <div
            key={i}
            className="card"
            style={{
              borderTop: `3px solid ${s.color}`,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/invoices')}
          >
            <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Invoices by month - Area chart */}
        <div className="card">
          <div className="card-header">
            <h3>Faktury wg miesiąca (ostatnie 12 mies.)</h3>
          </div>
          <div className="card-body" style={{ padding: '12px' }}>
            {data.invoicesByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.invoicesByMonth}>
                  <defs>
                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => {
                      const [y, m] = v.split('-');
                      return `${m}/${y.slice(2)}`;
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(label) => {
                      const parts = String(label).split('-');
                      return parts.length === 2 ? `${parts[1]}/${parts[0]}` : label;
                    }}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="amount"
                    stroke="#6366f1"
                    fill="url(#colorAmount)"
                    strokeWidth={2}
                  />
                  <Bar yAxisId="right" dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} opacity={0.7} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Brak danych do wyświetlenia
              </div>
            )}
          </div>
        </div>

        {/* Invoice status pie */}
        <div className="card">
          <div className="card-header">
            <h3>Status faktur</h3>
          </div>
          <div className="card-body" style={{ padding: '12px' }}>
            {invoicePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={invoicePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {invoicePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Brak faktur
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Orders pie + Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
        {/* Order status pie */}
        <div className="card">
          <div className="card-header">
            <h3>Status zamówień</h3>
          </div>
          <div className="card-body" style={{ padding: '12px' }}>
            {orderPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderPieData}
                    cx="50%"
                    cy="45%"
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {orderPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <span style={{ fontSize: '11px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Brak zamówień
              </div>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="card-header">
            <h3>Ostatnia aktywność</h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {data.recentActivity && data.recentActivity.length > 0 ? (
              <ul className="activity-list" style={{ margin: 0 }}>
                {data.recentActivity.map((activity, index) => (
                  <li
                    key={index}
                    className="activity-item"
                    style={{
                      cursor: activity.entityId ? 'pointer' : 'default',
                      padding: '10px 16px',
                      borderBottom: index < data.recentActivity.length - 1 ? '1px solid var(--border)' : 'none',
                      transition: 'background 0.15s',
                    }}
                    onClick={() => handleActivityClick(activity.type, activity.entityId)}
                    onMouseEnter={(e) => {
                      if (activity.entityId) e.currentTarget.style.background = 'var(--hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '';
                    }}
                  >
                    <div className="activity-content" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background:
                            activity.type === 'PurchaseOrder' ? '#3b82f6' :
                            activity.type === 'Shipment' ? '#10b981' :
                            activity.type === 'Invoice' ? '#f59e0b' :
                            '#8b5cf6',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <span className={`badge badge-${
                          activity.type === 'PurchaseOrder' ? 'new' :
                          activity.type === 'Shipment' ? 'shipped' :
                          activity.type === 'Invoice' ? 'pending' : 'active'
                        }`} style={{ marginRight: '8px', fontSize: '10px' }}>
                          {activity.type === 'PurchaseOrder' ? 'PO' :
                           activity.type === 'Shipment' ? 'Wysyłka' :
                           activity.type === 'Invoice' ? 'Faktura' :
                           'Sync'}
                        </span>
                        <span style={{ fontSize: '13px' }}>{activity.description}</span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {formatDate(activity.timestamp)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Brak aktywności
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
